import { useState, useEffect, useCallback, RefObject, useRef } from "react";
import Hls from "hls.js";
import { getPreferredTrack, parseM3u8MediaTracks } from "../../utils";
import { createHlsInstance } from "../../utils/hlsFactory";
import { IS_IOS_DEVICE } from "../../components/VideoPlayer/videoPlayer.utils";

// Expose our bundled hls.js as window.Hls so ReactPlayer's FilePlayer uses it
// instead of loading from CDN. This ensures player.hls is always available for
// audio/subtitle track switching on Safari desktop.
if (typeof window !== "undefined" && !(window as any).Hls) {
    (window as any).Hls = Hls;
}

// Called on every handleHls invocation so UA overrides in tests work correctly.
const isSafariNativeHls = (): boolean =>
    typeof navigator !== "undefined" && (
        /iPad|iPhone|iPod/.test(navigator.userAgent) ||
        /^((?!chrome|android).)*safari/i.test(navigator.userAgent)
    );

/**
 * Applies attributes required for AirPlay and inline playback directly on the
 * <video> element. ReactPlayer does not reliably forward non-standard attributes
 * (x-webkit-airplay, airplay), so we set them here.
 */
const applyVideoAttributes = (video: HTMLVideoElement): void => {
    if (video.dataset.epicAirplayReady === "1") return;

    (video as any).webkitAirPlayAllowed = true;
    video.setAttribute("x-webkit-airplay", "allow");
    video.setAttribute("airplay", "allow");

    // Ensure the browser does NOT disable AirPlay (some React wrappers set
    // disableRemotePlayback which silently blocks AirPlay on Safari/iOS)
    (video as any).disableRemotePlayback = false;
    video.removeAttribute("disableremoteplayback");

    video.setAttribute("playsinline", "");
    video.setAttribute("webkit-playsinline", "");
    (video as any).playsInline = true;

    video.setAttribute("preload", "auto");

    video.dataset.epicAirplayReady = "1";
};

const dbg = (...args: any[]) => console.log("[SubDebug]", ...args);

const logTextTracks = (video: HTMLVideoElement, label: string) => {
    const tracks = Array.from({ length: video.textTracks.length }, (_, i) => ({
        label: video.textTracks[i].label,
        language: video.textTracks[i].language,
        kind: video.textTracks[i].kind,
        mode: video.textTracks[i].mode,
    }));
    dbg(label, "textTracks:", tracks);
};

interface HlsMediaPreferences {
    preferredLanguage?: string | null;
}

export const useHlsMedia = (
    playerRef: RefObject<any>,
    preferences: HlsMediaPreferences = {},
) => {
    const hlsRef = useRef<Hls | null>(null);
    const lastHlsUrlRef = useRef<string>("");
    const lastVideoRef = useRef<HTMLVideoElement | null>(null);
    const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const nativeAudioNamesRef = useRef<string[]>([]);

    // Reactive HLS instance — triggers re-render in consumers (e.g. reApplyTracks effect)
    const [hlsInstanceState, setHlsInstanceState] = useState<Hls | null>(null);

    const [availableLanguages, setAvailableLanguages] = useState<string[]>([]);
    const [availableSubtitles, setAvailableSubtitles] = useState<string[]>([]);
    const [selectedLanguage, setSelectedLanguage] = useState<string>("");
    const [selectedSubtitle, setSelectedSubtitle] = useState<string>("");
    const [subtitlesEnabled, setSubtitlesEnabled] = useState<boolean>(false);

    // Mirror mutable values in refs so stale closures (event handlers, timeouts) read current values
    const subtitlesEnabledRef = useRef<boolean>(false);
    subtitlesEnabledRef.current = subtitlesEnabled;
    const selectedSubtitleRef = useRef<string>("");
    selectedSubtitleRef.current = selectedSubtitle;
    const selectedLanguageRef = useRef<string>("");
    selectedLanguageRef.current = selectedLanguage;
    const preferredLanguageRef = useRef<string | null | undefined>(preferences.preferredLanguage);

    useEffect(() => {
        preferredLanguageRef.current = preferences.preferredLanguage;
    }, [preferences.preferredLanguage]);

    // Sync setter for callers (e.g. iOS fullscreen) that need to align react-player
    // state with QuickTime's persisted preference. Updates the ref synchronously so
    // applyNativeSubtitles reads the new value on the same tick.
    const syncSubtitlesEnabled = useCallback((enabled: boolean) => {
        subtitlesEnabledRef.current = enabled;
        setSubtitlesEnabled(enabled);
    }, []);
    // Captured in handleHls before the state reset so the parseM3u8 async callback
    // can restore the user's audio selection after a fullscreen exit reinit.
    const pendingAudioLangRef = useRef<string>("");

    // Called by useIosFullscreen before handleHls when it detects which audio track
    // the user selected in QuickTime — takes precedence over the pre-enter state.
    const syncAudioLang = useCallback((lang: string) => {
        pendingAudioLangRef.current = lang;
    }, []);

    const destroyHls = useCallback(() => {
        if (retryTimerRef.current) {
            clearTimeout(retryTimerRef.current);
            retryTimerRef.current = null;
        }
        if (hlsRef.current) {
            hlsRef.current.destroy();
            hlsRef.current = null;
        }
        setHlsInstanceState(null);
        lastHlsUrlRef.current = "";
        lastVideoRef.current = null;
    }, []);

    const getVideoElement = useCallback((): HTMLVideoElement | null => {
        const internal = playerRef.current?.getInternalPlayer?.() as HTMLVideoElement | null;
        if (internal instanceof HTMLVideoElement) return internal;
        const domVideo = playerRef.current?.wrapper?.querySelector?.("video") as HTMLVideoElement | null;
        return domVideo ?? null;
    }, [playerRef]);

    // Periodic safety check: guarantees that whenever subtitles are disabled in menu,
    // any background hls.js or browser native textTracks setting mode = "showing"
    // is immediately disabled.
    useEffect(() => {
        const interval = setInterval(() => {
            if (subtitlesEnabledRef.current) return;

            const video = getVideoElement();
            const reactHls = (playerRef.current as any)?.getInternalPlayer?.("hls");

            if (reactHls && reactHls.subtitleTrack !== -1) {
                reactHls.subtitleTrack = -1;
            }
            if (hlsRef.current && hlsRef.current.subtitleTrack !== -1) {
                hlsRef.current.subtitleTrack = -1;
            }
            if (video?.textTracks && video.textTracks.length > 0) {
                for (let i = 0; i < video.textTracks.length; i++) {
                    if (video.textTracks[i].mode !== "disabled") {
                        video.textTracks[i].mode = "disabled";
                    }
                }
            }
        }, 400);

        return () => clearInterval(interval);
    }, [getVideoElement, playerRef]);

    const applyLanguageSelection = useCallback((lang: string): void => {
        if (!lang) return;

        // Tier 1: own hls.js instance (Chrome / Firefox)
        if (hlsRef.current) {
            const idx = hlsRef.current.audioTracks.findIndex((t) => t.name === lang);
            dbg("applyLanguageSelection: Tier1 (own hls), idx:", idx, lang);
            if (idx !== -1) hlsRef.current.audioTrack = idx;
            return;
        }

        // Tier 2: ReactPlayer's hls.js (Safari desktop + iOS MSE).
        const reactHls = (playerRef.current as any)?.getInternalPlayer?.("hls");
        if (reactHls) {
            const tracks: any[] = reactHls.audioTracks ?? [];
            dbg("applyLanguageSelection: Tier2 (reactHls), trackCount:", tracks.length, "isIOS:", IS_IOS_DEVICE, lang);
            if (tracks.length > 0) {
                const idx = tracks.findIndex((t: any) => t.name === lang);
                if (idx !== -1) {
                    if (IS_IOS_DEVICE) {
                        const video = getVideoElement();
                        const pos = video?.currentTime ?? 0;
                        reactHls.stopLoad();
                        reactHls.audioTrack = idx;
                        reactHls.startLoad(pos);
                        dbg("applyLanguageSelection: Tier2-iOS stopLoad+audioTrack+startLoad at pos:", pos);
                    } else {
                        reactHls.audioTrack = idx;
                    }
                }
                return;
            } else {
                const idx = nativeAudioNamesRef.current.indexOf(lang);
                if (idx !== -1) {
                    reactHls.audioTrack = idx;
                    return;
                }
            }
        }

        // Tier 3: native video.audioTracks (iOS Safari / fallback)
        const video = getVideoElement();
        if (!video) return;
        const audioTracks = (video as any).audioTracks;
        if (audioTracks?.length > 0) {
            const idx = nativeAudioNamesRef.current.indexOf(lang);
            if (idx !== -1 && idx >= audioTracks.length) return;
            for (let i = 0; i < audioTracks.length; i++) {
                audioTracks[i].enabled = idx !== -1
                    ? i === idx
                    : (audioTracks[i].label === lang || audioTracks[i].language === lang);
            }
        }
    }, [getVideoElement, playerRef]);

    const applyLanguageSelectionWithRetry = useCallback((lang: string, reason: string): void => {
        if (!lang) return;

        dbg("applyLanguageSelectionWithRetry", { lang, reason });
        applyLanguageSelection(lang);

        if (typeof window === "undefined") return;
        window.setTimeout(() => applyLanguageSelection(lang), 150);
        window.setTimeout(() => applyLanguageSelection(lang), 500);
    }, [applyLanguageSelection]);

    // Syncs the hls.js subtitle state to the current subtitlesEnabledRef value.
    // Always called regardless of enabled state — doApply checks the ref at
    // execution time to handle both deferred (once) and immediate paths.
    // This also prevents stale once-listeners from re-enabling subtitles after
    // the user disabled them and then exited iOS fullscreen (which triggers
    // SUBTITLE_TRACKS_UPDATED via loadSource).
    const reApplySubtitleToHls = useCallback((targetName: string, reactHlsInstance: any): void => {
        if (!reactHlsInstance) return;
        const doApply = () => {
            if (!subtitlesEnabledRef.current) {
                // hls.js auto-selects DEFAULT/AUTOSELECT tracks on loadSource;
                // override with -1 to honour the user's disabled preference.
                if (reactHlsInstance.subtitleTrack !== -1) {
                    reactHlsInstance.subtitleTrack = -1;
                    dbg("reApplySubtitleToHls: forced disabled (subtitlesEnabled=false)");
                }
                return;
            }
            const tracks: any[] = reactHlsInstance.subtitleTracks ?? [];
            const idx = tracks.findIndex((t: any) => t.name === targetName);
            const trackIdx = idx !== -1 ? idx : (tracks.length > 0 ? 0 : -1);
            if (trackIdx !== -1 && reactHlsInstance.subtitleTrack !== trackIdx) {
                reactHlsInstance.subtitleTrack = trackIdx;
                dbg("reApplySubtitleToHls: applied subtitleTrack =", trackIdx, "for:", targetName);
            } else if (trackIdx === -1) {
                dbg("reApplySubtitleToHls: no tracks found for:", targetName);
            }
        };

        doApply();

        if (typeof reactHlsInstance.off === "function") {
            reactHlsInstance.off(Hls.Events.SUBTITLE_TRACKS_UPDATED, doApply);
        }
        if (typeof reactHlsInstance.on === "function") {
            reactHlsInstance.on(Hls.Events.SUBTITLE_TRACKS_UPDATED, doApply);
        }
    }, [subtitlesEnabledRef]);

    // -------------------------------------------------------------------------
    // Native HLS path (Safari desktop + iOS Safari)
    // Populates track lists from ReactPlayer's hls.js instance when available,
    // falling back to manifest parsing and native browser APIs.
    // -------------------------------------------------------------------------
    const initNativeHlsTracks = useCallback((video: HTMLVideoElement, url: string): void => {
        const reactHls = (playerRef.current as any)?.getInternalPlayer?.("hls");
        const rtAudioTracks = reactHls?.audioTracks ?? [];
        const rtSubtitleTracks = reactHls?.subtitleTracks ?? [];

        dbg("initNativeHlsTracks", {
            hasReactHls: !!reactHls,
            rtAudioCount: rtAudioTracks.length,
            rtSubtitleCount: rtSubtitleTracks.length,
            rtSubtitles: rtSubtitleTracks.map((t: any) => ({ name: t.name, id: t.id })),
            subtitlesEnabled: subtitlesEnabledRef.current,
        });
        logTextTracks(video, "initNativeHlsTracks entry");

        if (rtAudioTracks.length > 0 || rtSubtitleTracks.length > 0) {
            // ReactPlayer's hls.js already has tracks — use them directly
            if (rtAudioTracks.length > 0) {
                const langs = rtAudioTracks.map((t: any) => t.name as string);
                const langToSelect = getPreferredTrack(langs, preferredLanguageRef.current) ?? langs[0];
                nativeAudioNamesRef.current = langs;
                setAvailableLanguages(Array.from(new Set(langs)));
                setSelectedLanguage(langToSelect);
                applyLanguageSelectionWithRetry(langToSelect, "react-hls-native-init");
            }
            if (rtSubtitleTracks.length > 0) {
                const subs = rtSubtitleTracks.map((t: any) => t.name as string);
                dbg("initNativeHlsTracks: using reactHls subtitle tracks:", subs);
                setAvailableSubtitles(Array.from(new Set(subs)));
                setSelectedSubtitle(subs[0]);
                reApplySubtitleToHls(subs[0], reactHls);
            }
            return;
        }

        // hls.js tracks not yet populated (timing) or iOS Safari (no hls.js).
        // Parse manifest for display names; nativeAudioNamesRef is used as
        // index source in handleLanguageChange even when reactHls exists.
        dbg("initNativeHlsTracks: falling back to parseM3u8MediaTracks");
        parseM3u8MediaTracks(url)
            .then(({ audio, subtitles }) => {
                dbg("parseM3u8MediaTracks result:", { audio, subtitles });
                if (audio.length > 0) {
                    nativeAudioNamesRef.current = audio;
                    setAvailableLanguages(audio);

                    // Restore the audio track the user had selected before the reinit
                    // (e.g. after iOS QuickTime fullscreen exit). pendingAudioLangRef was
                    // captured in handleHls before the state reset.
                    const preferred = pendingAudioLangRef.current || preferredLanguageRef.current;
                    pendingAudioLangRef.current = "";
                    const langToSelect = getPreferredTrack(audio, preferred) ?? audio[0];
                    dbg("parseM3u8: audio langToSelect:", langToSelect, "(preferred:", preferred, ")");
                    setSelectedLanguage(langToSelect);
                    applyLanguageSelectionWithRetry(langToSelect, "manifest-audio-init");

                    if (langToSelect !== audio[0]) {
                        const targetIdx = audio.indexOf(langToSelect);
                        const rHlsAudio = (playerRef.current as any)?.getInternalPlayer?.("hls");

                        const applyNativeAudio = () => {
                            const vid = getVideoElement();
                            if (!vid) return;
                            const nt = (vid as any).audioTracks;
                            if (nt?.length > targetIdx) {
                                for (let ai = 0; ai < nt.length; ai++) {
                                    nt[ai].enabled = ai === targetIdx;
                                }
                                dbg("parseM3u8: audio Tier3 applied idx:", targetIdx, langToSelect);
                            } else if (nt !== undefined) {
                                // tracks not yet populated — wait for loadedmetadata
                                vid.addEventListener("loadedmetadata", () => {
                                    const nt2 = (vid as any).audioTracks;
                                    if (nt2?.length > targetIdx) {
                                        for (let ai = 0; ai < nt2.length; ai++) {
                                            nt2[ai].enabled = ai === targetIdx;
                                        }
                                        dbg("parseM3u8: audio Tier3 deferred idx:", targetIdx, langToSelect);
                                    }
                                }, { once: true });
                                dbg("parseM3u8: audio Tier3 deferred (no tracks yet)", langToSelect);
                            }
                        };

                        if (rHlsAudio) {
                            const hlsTracks: any[] = rHlsAudio.audioTracks ?? [];
                            if (hlsTracks.length > 0) {
                                // Safari desktop: hls.js has tracks, switch via audioTrack setter
                                const hlsIdx = hlsTracks.findIndex((t: any) => t.name === langToSelect);
                                if (hlsIdx !== -1) rHlsAudio.audioTrack = hlsIdx;
                                dbg("parseM3u8: audio Tier2 applied idx:", hlsIdx, langToSelect);
                            } else {
                                // Timing gap or iOS native-HLS mode: audioTracks not yet populated.
                                if (targetIdx !== -1) rHlsAudio.audioTrack = targetIdx;
                                rHlsAudio.once(Hls.Events.AUDIO_TRACKS_UPDATED, () => {
                                    const tracks: any[] = rHlsAudio.audioTracks ?? [];
                                    if (tracks.length > 0) {
                                        const i = tracks.findIndex((t: any) => t.name === langToSelect);
                                        if (i !== -1) rHlsAudio.audioTrack = i;
                                        dbg("parseM3u8: audio Tier2 deferred idx:", i, langToSelect);
                                        return;
                                    }
                                    applyNativeAudio();
                                });
                                applyNativeAudio();
                            }
                        } else {
                            applyNativeAudio();
                        }
                    }
                }
                if (subtitles.length > 0) {
                    setAvailableSubtitles(subtitles);
                    setSelectedSubtitle(subtitles[0]);
                    dbg("parseM3u8: subtitle state after set — subtitlesEnabled:", subtitlesEnabledRef.current);
                    // Sync hls.js subtitle state — handles both re-enable after fullscreen exit
                    // and override of hls.js auto-select when subtitles are disabled.
                    const rHls = (playerRef.current as any)?.getInternalPlayer?.("hls");
                    if (rHls) {
                        reApplySubtitleToHls(subtitles[0], rHls);
                    }
                    const v = getVideoElement();
                    if (v && v.textTracks && !subtitlesEnabledRef.current) {
                        for (let i = 0; i < v.textTracks.length; i++) {
                            v.textTracks[i].mode = "disabled";
                        }
                    }
                }
            })
            .catch(() => void 0);

        const syncNativeTextTracks = () => {
            const textTracks = video.textTracks;
            if (!textTracks) return;
            if (!subtitlesEnabledRef.current) {
                for (let i = 0; i < textTracks.length; i++) {
                    textTracks[i].mode = "disabled";
                }
            }
        };

        const discoverNativeTracks = () => {
            // Don't overwrite names already set by parseM3u8MediaTracks
            if (nativeAudioNamesRef.current.length > 0) return;
            const nativeAudio = (video as any).audioTracks;
            // Only expose selector when there are multiple tracks — a single
            // muxed track has no meaningful label and nothing to switch to.
            if (nativeAudio?.length > 1) {
                const names: string[] = Array.from({ length: nativeAudio.length }, (_, i) =>
                    nativeAudio[i].label || nativeAudio[i].language || `Track ${i + 1}`
                );
                setAvailableLanguages(Array.from(new Set(names)));
                // Functional update avoids stale-closure dependency on selectedLanguage
                setSelectedLanguage((prev) => {
                    const langToSelect = prev || getPreferredTrack(names, preferredLanguageRef.current) || names[0];
                    applyLanguageSelectionWithRetry(langToSelect, "native-audio-discovery");
                    return langToSelect;
                });
            }
            const textTracks = video.textTracks;
            dbg("discoverNativeTracks (loadedmetadata):", {
                textTracksCount: textTracks?.length ?? 0,
                tracks: Array.from({ length: textTracks?.length ?? 0 }, (_, i) => ({
                    label: textTracks[i].label,
                    language: textTracks[i].language,
                    kind: textTracks[i].kind,
                    mode: textTracks[i].mode,
                })),
                subtitlesEnabled: subtitlesEnabledRef.current,
            });
            if (textTracks?.length > 0) {
                const names: string[] = Array.from({ length: textTracks.length }, (_, i) =>
                    textTracks[i].label || textTracks[i].language || `Subtitle ${i + 1}`
                );
                setAvailableSubtitles(Array.from(new Set(names)));
                setSelectedSubtitle((prev) => prev || names[0]);
                syncNativeTextTracks();
            }
        };
        video.addEventListener("loadedmetadata", discoverNativeTracks, { once: true });
        video.addEventListener("play", syncNativeTextTracks);
        video.addEventListener("playing", syncNativeTextTracks);
        if (typeof (video.textTracks as any)?.addEventListener === "function") {
            (video.textTracks as any).addEventListener("addtrack", syncNativeTextTracks);
        }
        if (video.readyState >= 1) discoverNativeTracks();
    }, [applyLanguageSelectionWithRetry, playerRef, reApplySubtitleToHls]);

    // -------------------------------------------------------------------------
    // Main entry point — call this whenever the HLS URL changes
    // -------------------------------------------------------------------------
    const handleHls = useCallback((
        url: string,
        options?: { isLiveContent?: boolean },
        attempt = 0
    ) => {
        if (!url) return;
        const isLiveContent = options?.isLiveContent ?? false;

        if (retryTimerRef.current) {
            clearTimeout(retryTimerRef.current);
            retryTimerRef.current = null;
        }

        const video = getVideoElement();
        if (!video) {
            if (attempt < 10) {
                retryTimerRef.current = setTimeout(() => handleHls(url, options, attempt + 1), 150);
            }
            return;
        }

        dbg("handleHls called", {
            attempt,
            urlSuffix: url.slice(-50),
            subtitlesEnabled: subtitlesEnabledRef.current,
            sameUrl: lastHlsUrlRef.current === url,
            sameVideo: lastVideoRef.current === video,
        });
        logTextTracks(video, "handleHls entry");

        if (lastHlsUrlRef.current === url && lastVideoRef.current === video) {
            dbg("handleHls: skipped (same url+video)");
            return;
        }

        // Preserve audio selection across reinits (e.g. iOS fullscreen exit).
        // If syncAudioLang already set pendingAudioLangRef (QuickTime audio detection
        // in useIosFullscreen), don't overwrite — the native selection takes precedence
        // over the react-player state captured before fullscreen enter.
        if (!pendingAudioLangRef.current) {
            pendingAudioLangRef.current = preferredLanguageRef.current || selectedLanguageRef.current || "";
        }

        destroyHls();
        applyVideoAttributes(video);
        setAvailableLanguages([]);
        setAvailableSubtitles([]);
        setSelectedLanguage("");
        setSelectedSubtitle("");
        setSubtitlesEnabled(false);
        subtitlesEnabledRef.current = false;
        dbg("handleHls: state reset — subtitlesEnabled reset to false (deactivated by default)");

        if (!isSafariNativeHls() && Hls.isSupported()) {
            const hls = createHlsInstance(video, url, isLiveContent, {
                onTracksReady: (languages, subtitles) => {
                    setAvailableLanguages(languages);
                    setAvailableSubtitles(subtitles);
                    if (languages.length > 0) {
                        const langToSelect = getPreferredTrack(languages, preferredLanguageRef.current) ?? languages[0];
                        setSelectedLanguage(langToSelect);
                        applyLanguageSelectionWithRetry(langToSelect, "hls-tracks-ready");
                    }
                    if (subtitles.length > 0) {
                        setSelectedSubtitle(subtitles[0]);
                        if (!subtitlesEnabledRef.current) {
                            hls.subtitleTrack = -1;
                        } else {
                            const idx = hls.subtitleTracks.findIndex((track) => track.name === subtitles[0]);
                            if (idx !== -1) hls.subtitleTrack = idx;
                        }
                    }
                },
                onFatalError: destroyHls,
            });
            hls.on(Hls.Events.SUBTITLE_TRACKS_UPDATED, () => {
                if (!subtitlesEnabledRef.current && hls.subtitleTrack !== -1) {
                    hls.subtitleTrack = -1;
                }
            });
            hlsRef.current = hls;
            setHlsInstanceState(hls);
            lastHlsUrlRef.current = url;
            lastVideoRef.current = video;
        } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
            // On Safari desktop, ReactPlayer's FilePlayer uses our bundled hls.js (via window.Hls).
            // Do NOT set video.src here — ReactPlayer already manages the source.
            initNativeHlsTracks(video, url);
            lastHlsUrlRef.current = url;
            lastVideoRef.current = video;
        }
    }, [applyLanguageSelectionWithRetry, getVideoElement, destroyHls, initNativeHlsTracks]);

    // -------------------------------------------------------------------------
    // Track switching — three tiers: own hls.js → ReactPlayer hls.js → native
    // -------------------------------------------------------------------------

    const handleLanguageChange = useCallback((event: any) => {
        const lang = event.target.value as string;
        dbg("handleLanguageChange", { lang });
        setSelectedLanguage(lang);
        applyLanguageSelection(lang);
    }, [applyLanguageSelection]);

    const handleSubtitleChange = useCallback((event: any) => {
        const sub = event.target.value as string;
        dbg("handleSubtitleChange", { sub, subtitlesEnabled });
        setSelectedSubtitle(sub);
        if (!subtitlesEnabled) return;

        // Tier 1
        if (hlsRef.current) {
            const idx = hlsRef.current.subtitleTracks.findIndex((t) => t.name === sub);
            if (idx !== -1) hlsRef.current.subtitleTrack = idx;
            return;
        }

        // Tier 2
        const reactHls = (playerRef.current as any)?.getInternalPlayer?.("hls");
        if (reactHls?.subtitleTracks?.length > 0) {
            const idx = reactHls.subtitleTracks.findIndex((t: any) => t.name === sub);
            if (idx !== -1) reactHls.subtitleTrack = idx;
            return;
        }

        // Tier 3
        const video = getVideoElement();
        if (!video) return;
        const textTracks = video.textTracks;
        for (let i = 0; i < textTracks.length; i++) {
            const track = textTracks[i];
            const isMatch =
                track.label === sub ||
                track.language === sub ||
                track.label?.toLowerCase() === sub.toLowerCase();
            track.mode = isMatch ? "showing" : "disabled";
        }
    }, [subtitlesEnabled, getVideoElement]);

    const handleSubtitleToggle = useCallback(() => {
        setSubtitlesEnabled((prev) => {
            const next = !prev;
            const hls = hlsRef.current ?? (playerRef.current as any)?.getInternalPlayer?.("hls");
            const video = getVideoElement();

            dbg("handleSubtitleToggle", {
                prev,
                next,
                selectedSubtitle,
                hasHlsRef: !!hlsRef.current,
                hasReactHls: !!(playerRef.current as any)?.getInternalPlayer?.("hls"),
                hlsSubtitleTracks: hls?.subtitleTracks?.map?.((t: any) => ({ name: t.name, id: t.id })) ?? "no-hls",
                hlsCurrentSubtitleTrack: hls?.subtitleTrack ?? "no-hls",
            });
            if (video) logTextTracks(video, "handleSubtitleToggle before change");

            if (hls) {
                if (next) {
                    const tracksList = hls.subtitleTracks ?? [];
                    const idx = tracksList.findIndex((t: any) => t.name === selectedSubtitle);
                    const trackIdx = idx !== -1 ? idx : (tracksList.length > 0 ? 0 : -1);
                    dbg("handleSubtitleToggle: enabling via hls", {
                        selectedSubtitle,
                        trackIdx,
                        idxFromFind: idx,
                        subtitleTracksLength: tracksList.length,
                        subtitleTracksNames: tracksList.map((t: any) => t.name),
                        currentSubtitleTrackBefore: hls.subtitleTrack,
                    });
                    hls.subtitleTrack = trackIdx;
                    dbg("handleSubtitleToggle: enabling via hls AFTER set", {
                        currentSubtitleTrackAfter: hls.subtitleTrack,
                    });
                    if (video) {
                        setTimeout(() => logTextTracks(video, "handleSubtitleToggle 100ms after enable"), 100);
                    }
                } else {
                    dbg("handleSubtitleToggle: disabling via hls");
                    hls.subtitleTrack = -1;
                }
            } else {
                const textTracks = video?.textTracks;
                if (textTracks) {
                    for (let i = 0; i < textTracks.length; i++) {
                        const track = textTracks[i];
                        const isSelectedTrack =
                            track.label === selectedSubtitle ||
                            track.language === selectedSubtitle ||
                            (selectedSubtitle &&
                                track.label?.toLowerCase() === selectedSubtitle.toLowerCase());
                        const newMode: TextTrackMode = next && isSelectedTrack ? "showing" : "disabled";
                        dbg(`handleSubtitleToggle: textTrack[${i}] "${track.label}" mode ${track.mode} → ${newMode} (isSelected: ${isSelectedTrack})`);
                        track.mode = newMode;
                    }
                } else {
                    dbg("handleSubtitleToggle: no hls and no textTracks — cannot apply toggle");
                }
            }
            return next;
        });
    }, [selectedSubtitle, getVideoElement]);

    // -------------------------------------------------------------------------
    // Effects
    // -------------------------------------------------------------------------

    useEffect(() => {
        const video = getVideoElement();
        if (!video) return;
        const onError = (e: Event) =>
            console.error("Video element error:", (e.target as HTMLVideoElement).error);
        video.addEventListener("error", onError);
        return () => video.removeEventListener("error", onError);
    }, [getVideoElement]);

    useEffect(() => () => destroyHls(), [destroyHls]);

    // Re-apply AirPlay attributes once after mount
    useEffect(() => {
        const video = getVideoElement();
        if (video) applyVideoAttributes(video);
    }, [getVideoElement]);

    return {
        hlsInstance: hlsInstanceState,
        hlsRef,
        availableLanguages,
        availableSubtitles,
        selectedLanguage,
        selectedSubtitle,
        subtitlesEnabled,
        subtitlesEnabledRef,
        selectedSubtitleRef,
        selectedLanguageRef,
        audioOrderRef: nativeAudioNamesRef,
        syncSubtitlesEnabled,
        syncAudioLang,
        handleHls,
        handleLanguageChange,
        handleSubtitleChange,
        handleSubtitleToggle,
        destroyHls,
    };
};
